import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApi } from '@/lib/api-context';

import {
  commentResponseSchema,
  commentsResponseSchema,
  type Comment,
  type CommentStatus,
} from './types';

// Comentarios del documento (control de cambios). Reales: listar, agregar, resolver.
// Cada mutación invalida la lista para reflejar el cambio sin recargar.

const DOCS = '/api/v1/quality/documents';

interface Options {
  companyId: string | null;
  enabled: boolean;
}

export function useComments(
  id: string,
  { companyId, enabled }: Options,
): UseQueryResult<Comment[]> {
  const api = useApi();
  return useQuery({
    queryKey: ['comments', companyId, id],
    queryFn: async () => {
      const raw = await api.get<unknown>(`${DOCS}/${id}/comments`);
      return commentsResponseSchema.parse(raw).data;
    },
    enabled,
  });
}

export function useAddComment(
  id: string,
): UseMutationResult<Comment, Error, { body: string; sectionHint?: string }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, sectionHint }) => {
      const payload = sectionHint === undefined ? { body } : { body, sectionHint };
      const raw = await api.post<unknown>(`${DOCS}/${id}/comments`, payload);
      return commentResponseSchema.parse(raw).data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['comments'] }),
  });
}

export function useResolveComment(
  id: string,
): UseMutationResult<Comment, Error, { commentId: string; status: CommentStatus }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ commentId, status }) => {
      const raw = await api.patch<unknown>(`${DOCS}/${id}/comments/${commentId}`, { status });
      return commentResponseSchema.parse(raw).data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['comments'] }),
  });
}
